import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LoadBoardHero } from '@/components/loadboard/LoadBoardHero';
import { LoadFilters } from '@/components/loadboard/LoadFilters';
import { LoadsTable } from '@/components/loadboard/LoadsTable';
import { WhyPartnerSection } from '@/components/loadboard/WhyPartnerSection';
import { HotLanesStrip } from '@/components/HotLanesStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoadBoard = () => {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cta, setCta] = useState(null);
  const [meta, setMeta] = useState(null);
  const [filters, setFilters] = useState({
    origin: '',
    destination: '',
    truckType: '',
    status: 'available'
  });
  const [email, setEmail] = useState('');
  const [otr, setOtr] = useState(true);
  const [drayage, setDrayage] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.origin) params.append('origin', filters.origin);
      if (filters.destination) params.append('destination', filters.destination);
      if (filters.truckType) params.append('truck_type', filters.truckType);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`${API}/loads?${params.toString()}`);
      setLoads(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching loads:', error);
      }
      toast.error('Failed to fetch loads');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLoads();
    axios.get(`${API}/loadboard-cta`).then(res => setCta(res.data)).catch(() => {});
    axios.get(`${API}/loads/meta`).then(res => setMeta(res.data)).catch(() => {});
  }, [fetchLoads]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLoads();
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    let carrierType = '';
    if (otr) carrierType = 'OTR';
    if (drayage) carrierType = 'DRAYAGE';

    if (!carrierType) {
      toast.error('Please select a load type (OTR or Drayage)');
      return;
    }

    setSubscribing(true);
    try {
      await axios.post(`${API}/carriers/register`, {
        email,
        carrier_type: carrierType,
      });
      toast.success('Subscribed! You\'ll receive load notifications.');
      setEmail('');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to subscribe';
      toast.error(msg);
    } finally {
      setSubscribing(false);
    }
  };

  const clearFilters = () => {
    setFilters({ origin: '', destination: '', truckType: '', status: 'available' });
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <HotLanesStrip theme="light" />
      <LoadFilters
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        onSearch={handleSearch}
        onClear={clearFilters}
      />
      <LoadsTable
        loads={loads}
        loading={loading}
        onRefresh={fetchLoads}
        onClear={clearFilters}
        meta={meta}
      />
      {cta?.enabled && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-zinc-900 border border-zinc-800 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="loadboard-cta">
            <span className="text-zinc-300 text-sm font-medium" data-testid="cta-badge">{cta.badge_text}</span>
            {cta.url && (
              <a
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm transition-all hover:-translate-y-0.5"
                data-testid="cta-button"
              >
                {cta.label || 'Learn More'}
              </a>
            )}
          </div>
        </div>
      )}
      <LoadBoardHero
        email={email}
        setEmail={setEmail}
        otr={otr}
        setOtr={setOtr}
        drayage={drayage}
        setDrayage={setDrayage}
        subscribing={subscribing}
        onSubscribe={handleSubscribe}
      />
      <WhyPartnerSection />
    </div>
  );
};

export default LoadBoard;
